import { useEffect, useRef, useState } from "react";
import { PetCanvas } from "../render/PetCanvas";
import { Dock } from "./Dock";
import { EditBar } from "./EditBar";
import { HatchPanel } from "./HatchPanel";
import { HatchSequence } from "./HatchSequence";
import { METERS_META, type MeterId } from "./icons";
import { Meters } from "./Meters";
import { SpotPicker, type SpotKind } from "./SpotPicker";
import { Toasts } from "./Toasts";
import { Zones } from "./Zones";
import type {
  BadgeView,
  BookEntryView,
  LookKind,
  LookOptionView,
  LooksView,
  MenuId,
  MeterView,
  Mood,
  PreviousPetView,
  SettingsView,
  ToastView,
} from "./view-model";

export interface LcdProps {
  status: "no-pet" | "alive" | "dead";
  species: string;
  petName: string;
  meters: MeterView[];
  score: number;
  mood: Mood;
  sleeping?: boolean;
  settings: SettingsView;
  reducedMotion: boolean;
  toasts: ToastView[];
  selectedAction: number;
  looks?: LooksView;
  options?: Record<LookKind, LookOptionView[]>;
  editing?: boolean;
  /** Last week's pet: the Monday hatch sequence plays before the naming panel. */
  previous?: PreviousPetView | null;
  /** A visit: no score, no meters. */
  visiting?: BookEntryView | null;
  /** The Monday sequence has played or was skipped. Held by the screen so it plays once. */
  sequenceDone?: boolean;
  /** Active settings sheet: "badges" or "trophies" or null. */
  activeSettingsSheet?: "settings" | "name" | "account" | "friends" | "badges" | "shop" | "more" | "trophies" | null;
  badges?: BadgeView[];
  dripWeeksLeft?: number | null;
  /** Must be stable between renders. */
  onSequenceDone?(): void;
  onCare(meter: MeterId): void;
  onHatch(name: string): void;
  onDismissToast(id: number): void;
  onLeaveVisit?(): void;
  onPickLook?(kind: LookKind, index: number): void;
  onMenu?(id: MenuId): void;
  onCloseSettingsSheet?(): void;
}

function worstActionName(meters: MeterView[]): string {
  const byId = new Map(meters.map((m) => [m.id, m.value]));
  let worst = METERS_META[0]!;
  let minVal = byId.get(worst.k) ?? 100;
  for (const meta of METERS_META) {
    const val = byId.get(meta.k) ?? 100;
    if (val < minVal) {
      minVal = val;
      worst = meta;
    }
  }
  return worst.a;
}

export function Lcd({
  status,
  species,
  petName,
  meters,
  score,
  mood,
  sleeping,
  settings,
  reducedMotion,
  toasts,
  selectedAction,
  looks,
  options,
  editing = false,
  previous = null,
  visiting = null,
  sequenceDone = false,
  activeSettingsSheet = null,
  onSequenceDone = () => {},
  onCare,
  onHatch,
  onDismissToast,
  onLeaveVisit,
  onPickLook,
  onMenu,
}: LcdProps) {
  const [activeSpot, setActiveSpot] = useState<SpotKind | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const handleSelectSpot = (spot: SpotKind) => {
    openerRef.current = document.activeElement as HTMLElement | null;
    setActiveSpot((current) => (current === spot ? null : spot));
  };

  const closeSpotPicker = () => {
    setActiveSpot(null);
    openerRef.current?.focus();
  };

  const currentSpot = editing ? activeSpot : null;

  useEffect(() => {
    if (!currentSpot) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // Capture phase and immediate stop: GameScreen's window listener would otherwise leave edit mode as well.
        e.stopImmediatePropagation();
        closeSpotPicker();
      }
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [currentSpot]);

  const visit = visiting !== null;
  const sequence = !visit && status === "no-pet" && previous !== null && !sequenceDone;
  const warn = !visit && status === "alive" && mood === "lastChance";
  const paddedScore = String(Math.max(0, Math.floor(score))).padStart(4, "0");
  const worstAction = worstActionName(meters);
  const latestToast = toasts.length > 0 ? toasts[toasts.length - 1]?.text ?? null : null;

  const lcdClass = [
    "lcd",
    warn ? "warn" : "",
    settings.largeText ? "large" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={lcdClass} id="lcd" data-testid="lcd">
      <div className="glare" aria-hidden="true" />
      {visit ? (
        <div className="visitbar" data-testid="visit-bar">
          <span>{`Visiting ${visiting.name}. No score, no meters.`}</span>
          <button className="btn" type="button" data-testid="visit-back" onClick={onLeaveVisit}>
            Back to book
          </button>
        </div>
      ) : (
        <>
          <div className="score px" id="scoreRow">
            <div className="srow">
              <div className="odo" data-testid="score">
                <span className="sl">SCORE</span>
                <span className="digs" id="digs" data-testid="score-value">
                  {paddedScore}
                </span>
              </div>
              <span className="pn" id="pn" data-testid="pet-name">
                {petName}
              </span>
            </div>
          </div>

          <Meters
            meters={meters}
            meterStyle={settings.meterStyle}
            meterLabels={settings.meterLabels}
          />
        </>
      )}

      <div
        className="stage"
        id="stage"
        onClick={(e) => {
          if (
            !editing &&
            !activeSpot &&
            !activeSettingsSheet &&
            (visit || status === "alive") &&
            !sequence &&
            settings.controls === "touch"
          ) {
            const target = e.target as HTMLElement;
            if (target === e.currentTarget || target.tagName === "CANVAS") {
              onCare("fun");
            }
          }
        }}
      >
        {sequence ? (
          <HatchSequence previous={previous} species={species} reducedMotion={reducedMotion} onDone={onSequenceDone} />
        ) : (
          <PetCanvas
            species={visiting?.species ?? species}
            petName={visiting?.name ?? petName}
            mood={mood}
            reducedMotion={reducedMotion}
            looks={visiting?.looks ?? looks}
            sleeping={sleeping}
          />
        )}
        {editing && !sequence && !visit ? (
          <>
            <Zones
              onSelectSlot={(slot) => handleSelectSpot(slot)}
              onSelectShell={() => handleSelectSpot("shell")}
              onSelectWater={() => handleSelectSpot("water")}
            />
            {currentSpot && options ? (
              currentSpot === "shell" ? (
                <SpotPicker
                  kind="shell"
                  shellColours={options.shellColour ?? []}
                  shellPatterns={options.shellPattern ?? []}
                  onPick={onPickLook ?? (() => {})}
                  onClose={closeSpotPicker}
                />
              ) : (
                <SpotPicker
                  kind={currentSpot}
                  options={
                    currentSpot === "water"
                      ? (options.background ?? [])
                      : (options[currentSpot] ?? [])
                  }
                  onPick={onPickLook ?? (() => {})}
                  onClose={closeSpotPicker}
                />
              )
            ) : null}
          </>
        ) : null}
        {warn ? (
          <div className="warnbar" id="warnbar" data-testid="warnbar">
            {`${petName || "Your pet"} needs a little TLC soon. ${
              settings.controls === "retro"
                ? `Select ${worstAction} and press B.`
                : `Tap ${worstAction} to help.`
            }`}
          </div>
        ) : null}
        {!editing ? <Toasts toasts={toasts} onDismissToast={onDismissToast} /> : null}
        {!visit && status === "dead" ? (
          <div className="gone" id="gone" data-testid="dead-banner" role="status">
            <b>{`${petName} didn't make it. The points stay.`}</b>
          </div>
        ) : null}
      </div>

      <div id="controls">
        {sequence ? null : status === "no-pet" && !visit ? (
          <HatchPanel onHatch={onHatch} />
        ) : editing ? (
          <EditBar
            latestToast={latestToast}
            onDone={() => onMenu?.("items")}
          />
        ) : (
          <Dock
            controls={settings.controls}
            disabled={!visit && status !== "alive"}
            petName={visiting?.name ?? petName}
            selectedIndex={selectedAction}
            onCare={onCare}
          />
        )}
      </div>
    </div>
  );
}
